
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Activity } from 'lucide-react';

interface StatusChartProps {
  data: Array<{
    name: string;
    value: number;
    color: string;
  }>;
}

const StatusChart = ({ data }: StatusChartProps) => {
  const hasData = data.some(item => item.value > 0);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (!hasData) {
    return (
      <Card className="card-lift border-2 border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-display font-bold flex items-center gap-2 text-blue-600">
            <Activity className="h-5 w-5 text-blue-600" />
            Status dos Tickets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-slate-500">
            <p className="font-medium">Nenhum ticket encontrado</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-lift border-2 border-slate-200 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
      <CardHeader className="relative">
        <CardTitle className="text-lg font-display font-bold flex items-center gap-2 text-blue-600">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Activity className="h-5 w-5 text-blue-600" />
          </div>
          Status dos Tickets
        </CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value, 'Tickets']} />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          {data.filter((d) => d.value > 0).map((entry, index) => (
            <div 
              key={entry.name} 
              className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg hover:bg-slate-100 transition-all duration-300 animate-fade-in"
              style={{ '--tw-animation-delay': `${index * 0.1}s` } as React.CSSProperties}
            >
              <span
                className="inline-block h-4 w-4 rounded-md shadow-sm"
                style={{ backgroundColor: entry.color }}
                aria-hidden
              />
              <span className="text-sm font-semibold text-slate-700">
                {entry.name}
              </span>
              <span className="text-xs font-bold text-slate-900 bg-white px-2 py-1 rounded-md">
                {entry.value} ({total ? `${Math.round((entry.value / total) * 100)}%` : '0%'})
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default StatusChart;
